import { forwardRef, useState } from "react";

import * as Sentry from "@sentry/nextjs";
import { useTranslations } from "next-intl";

import { latexGeneratePdf, getApiErrorMessage } from "@/api";
import { AlertDialog } from "@/components/dialog/alert/AlertDialog";
import { Icon } from "@/components/icons/Icon";
import { useStore } from "@/store";
import { downloadFile } from "@/utils/downloadFile";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import type { ComponentProps } from "react";

export const DownloadPDFButton = forwardRef<
  HTMLButtonElement,
  { docId?: string } & ComponentProps<"button">
>(function DownloadPDFButton({ docId, onClick = () => {}, ...props }, ref) {
  const t = useTranslations("sidebar.downloadMenu");
  const buttonTitle = t("downloadPDF");
  const [pdfStatus, setPdfStatus] = useState<"idle" | "downloading" | "error">("idle");
  const disabled = pdfStatus === "downloading";
  const [error, setError] = useState("");

  const { auth, dialog } = useStore();

  const handleDownloadPDFButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (!auth.isLoggedIn) {
      dialog.loginDialog.open();
      return;
    }

    if (pdfStatus === "downloading" || !docId) return;

    // Track download PDF button click
    trackEvent("download_pdf_clicked", { doc_id: docId });

    setPdfStatus("downloading");

    try {
      const pdfRes = await latexGeneratePdf({ docId });

      if (pdfRes instanceof Blob) {
        const fileName = (pdfRes instanceof File && pdfRes.name) || `document.pdf`;
        downloadFile(pdfRes, fileName);
      } else {
        console.warn("PDF response is not a Blob", pdfRes);
      }
      onClick(e);
      setPdfStatus("idle");
    } catch (err) {
      setError(getApiErrorMessage(err));
      setPdfStatus("error");
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <button
        {...props}
        ref={ref}
        data-testid="download-pdf-button"
        type="button"
        disabled={disabled}
        aria-label={buttonTitle}
        onClick={handleDownloadPDFButtonClick}
        className={twMerge(
          "w-full h-8.5 px-2.5 text-sm flex items-center rounded-8 whitespace-nowrap tracking-tight",
          "sm:h-auto sm:py-2 sm:text-size-17",
          !disabled && "hover:bg-sidebar-button-active-bg data-[focus]:bg-sidebar-button-active-bg",
          disabled && "opacity-50",
        )}
      >
        <div className="mr-2.5">
          {pdfStatus === "downloading" ? (
            <Icon name="Spinner" className="animate-spin inline-block" />
          ) : (
            <Icon name="download/DownloadPDF" />
          )}
        </div>
        <span className="inline">
          <span>{buttonTitle}</span>
        </span>
      </button>
      <AlertDialog
        isOpen={pdfStatus === "error"}
        title={t("downloadPDFError")}
        description={error}
        onClose={() => {
          setPdfStatus("idle");
          setError("");
        }}
      />
    </>
  );
});
